import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { AccountManager, btag, debounce, pushToDataLayer, SiteConfigManager } from '@atlas/core';
import { Button, Icon } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import { useCasinoFilterState } from 'components/Casino/CasinoFilterContext';
import { useCasinoSearchPosthogContext } from 'hooks/usePostHog';
import {
  casinoFilterTypeMap,
  deleteCasinoFilter,
  getFilter,
  updateCasinoFilter,
} from 'components/Casino/useCasinoFilter';

export const MIN_SEARCH_COUNT = 3;

const CasinoSearch = ({ isSearchActive, loading, setIsSearchActive }) => {
  const { updateFilter } = useCasinoFilterState();
  const { handleClear, handleSearchRequest } = useCasinoSearchPosthogContext();
  const searchInputEl = useRef(null);
  const searchQuery = getFilter(casinoFilterTypeMap.name) || '';

  const t = useTranslate();
  const homePageFunctionality = SiteConfigManager.getConfig(
    'config.config.casino.desktop_home_page',
  );

  const [val, setVal] = useState(searchQuery);
  const [disabled, setDisabled] = useState(searchQuery.length < MIN_SEARCH_COUNT);
  const [showMessage, setShowMessage] = useState(!searchQuery.length);

  const onChange = useCallback(
    e => {
      const { value } = e.target;

      if (!value) {
        cleanSearch();
        return;
      }

      setVal(value);
      setDisabled(value.length < MIN_SEARCH_COUNT);
      setShowMessage(!searchQuery && value.length < MIN_SEARCH_COUNT);

      if (
        value.length >= MIN_SEARCH_COUNT ||
        (value.length < MIN_SEARCH_COUNT && val.length === MIN_SEARCH_COUNT)
      ) {
        searchHandler();
      }
    },
    [val, searchQuery],
  );

  const backToCasino = useCallback(() => {
    setIsSearchActive(false);
    updateFilter({ name: '' });
    deleteCasinoFilter(casinoFilterTypeMap.name);
  }, []);

  const cleanSearch = useCallback(() => {
    deleteCasinoFilter(casinoFilterTypeMap.name);
    setVal('');
    setDisabled(true);
    setShowMessage(true);
    handleClear();
  }, []);

  const searchHandler = useCallback(
    debounce(() => {
      const { clientId } = AccountManager.account;
      const { value } = searchInputEl.current;

      updateCasinoFilter(casinoFilterTypeMap.name, value);
      updateFilter({ name: value });
      setShowMessage(false);
      handleSearchRequest(value);

      pushToDataLayer(
        'casino_search_request',
        { client: { id: clientId } },
        {
          name: value,
        },
        btag,
      );
    }, 250),
    [],
  );

  const openSearchHandler = useCallback(() => {
    if (searchInputEl.current) {
      searchInputEl.current.focus();
    }
    setIsSearchActive(true);
  }, []);

  useEffect(() => {
    if (homePageFunctionality && isSearchActive) {
      openSearchHandler();
    }
  }, [homePageFunctionality, isSearchActive, openSearchHandler]);

  const onKeyUp = useCallback(
    ({ which }) => {
      // 13 is enter
      if (which === 13) {
        searchHandler();
      }
    },
    [searchHandler],
  );

  return (
    <div className="casino-search">
      <button className="back-to-container ellipsis" onClick={backToCasino}>
        <Icon className="icon-back" name="back" />
      </button>

      <div className="casino-search__wrapper">
        <div className="casino-search__field">
          <label className="casino-search__label" htmlFor="casino-search-input">
            <Icon name="search" />
          </label>

          <input
            autoFocus
            className="casino-search__input ui__field"
            id="casino-search-input"
            maxLength="50"
            onChange={onChange}
            onKeyUp={onKeyUp}
            placeholder={t('search_games')}
            ref={searchInputEl}
            type="text"
            value={val}
          />

          {val.length > 0 && (
            <button className="casino-search__close" onClick={cleanSearch} type="button">
              <Icon name="close" />
            </button>
          )}
        </div>
        {showMessage && (
          <div className="casino-search__message">{t('casino_search_initial_title')}</div>
        )}
      </div>

      <Button
        disabled={disabled}
        label="search"
        loading={loading}
        onClick={searchHandler}
        type="primary"
      />
    </div>
  );
};

CasinoSearch.propTypes = {
  isSearchActive: PropTypes.bool,
  loading: PropTypes.bool,
  setIsSearchActive: PropTypes.func,
};

export default CasinoSearch;
